import {
  darkThemeStyles,
  format,
  JupiterTheme,
  LineChart,
} from '@jupiterone/jupiter-web-toolkit';
import numeral from 'numeral';
import React from 'react';
import { UsageDataPoint } from 'navbar/src/jupiterAccountSettings/types';
import colorByUsageMetric from 'navbar/src/jupiterAccountSettings/util/colorByUsageMetric';
import { TooltipProps } from 'recharts';
import { createStyles, makeStyles } from '@material-ui/styles';

const COLOR_LIGHT = '#FFF';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    ...darkThemeStyles,
    tooltipRoot: {
      background: theme.palette.background.paper,
      padding: theme.spacing(0.5),
    },
    tooltipTitle: {
      paddingBottom: theme.spacing(1),
    },
    tooltipList: {
      listStyleType: 'none',
      padding: 0,
      margin: theme.spacing(0.5),
    },
    timePoint: {
      color: theme.palette.text.primary,
    },
  })
);

interface DataPoint {
  time: number;
  entityCount?: number;
  billableEntityCount?: number;
  relationshipCount?: number;
}

interface AccountManagementChartProps {
  usageDataPoints: UsageDataPoint[];
}

const formatTime = (time: number | string, label?: string) => {
  const dateString = format(new Date(time), 'MM/dd');
  return label ? `${label}: ${dateString}` : dateString;
};

// Recharts defines this input as any
const formatValue = (value: any) => {
  return numeral(value).format('0.00a');
};

const formatTooltip = (dataPoint: number | string | (string | number)[]) => {
  // This will only be a number or string, we know because we supply the data
  if (typeof dataPoint === 'string') {
    return Math.round(parseFloat(dataPoint)).toLocaleString();
  } else {
    return Math.round(dataPoint as number).toLocaleString();
  }
};

const AccountManagementChart = ({
  usageDataPoints,
}: AccountManagementChartProps) => {
  const classes = useStyles();
  const data: DataPoint[] = usageDataPoints.map((usageDataPoint) => {
    const entityCount = usageDataPoint.metrics['entityCount'];
    const relationshipCount = usageDataPoint.metrics['relationshipCount'];
    const billableEntityCount = usageDataPoint.metrics['billableEntityCount'];

    const time = usageDataPoint.endTimestamp;
    return { entityCount, relationshipCount, billableEntityCount, time };
  });

  return (
    <LineChart<DataPoint>
      height={500}
      data={data}
      grid={{ strokeDasharray: '5', stroke: '#555' }}
      x={{
        name: 'Day',
        key: 'time',
        formatter: formatTime,
        stroke: COLOR_LIGHT,
      }}
      y={{
        keys: [
          {
            key: 'entityCount',
            name: 'Entity Count',
            color: colorByUsageMetric('entityCount'),
          },
          {
            key: 'billableEntityCount',
            name: 'Billable Entity Count',
            color: colorByUsageMetric('billableEntityCount'),
          },
        ],
        formatter: formatValue,
        name: 'Values',
        stroke: COLOR_LIGHT,
        padding: { top: 0, bottom: 30 },
      }}
      tooltip={{
        content: (props: TooltipProps) => {
          // Need to do this inline to be able to have access to the
          // classes.  This gets called by recharts, so I can't pass
          // other props in.  Recharts doesn't use functional components,
          // so if we break this into its own component (where recharts)
          // is the parent, react freaks out because its calling a
          // hook in a non-functional component.
          if (
            props.payload &&
            Array.isArray(props.payload) &&
            props.payload.length
          ) {
            const sortedPayloadValues = props.payload.sort(
              (payloadLeft, payloadRight) => {
                return payloadLeft.value > payloadRight.value ? -1 : 1;
              }
            );
            return (
              <div className={classes.tooltipRoot}>
                <ol className={classes.tooltipList}>
                  <>
                    <li className={classes.tooltipTitle}>
                      {formatTime(sortedPayloadValues[0].payload.time, 'Date')}
                    </li>
                    {sortedPayloadValues.map((i, index) => {
                      return (
                        <li
                          key={index}
                          style={{
                            color: i.color,
                          }}
                        >
                          {`${i.name}: ${formatTooltip(i.value)}`}
                        </li>
                      );
                    })}
                  </>
                </ol>
              </div>
            );
          }

          return null;
        },
      }}
      legend={{
        layout: 'vertical',
        align: 'center',
        verticalAlign: 'top',
        // this is a hack to get recharts to render its height
        // correctly and not have the legend push the chart to the side
        wrapperStyle: { right: 0, top: 0 },
        // eslint-disable-next-line react/display-name
        formatter: (val) => <span style={{ color: COLOR_LIGHT }}>{val}</span>,
      }}
    />
  );
};
export default AccountManagementChart;
